import {
  JsonFormsRendererRegistryEntry,
  and,
  formatIs,
  isArrayObjectControl,
  isBooleanControl,
  isObjectArrayWithNesting,
  isObjectControl,
  isOneOfControl,
  isOneOfEnumControl,
  optionIs,
  or,
  rankWith,
  schemaTypeIs,
  uiTypeIs,
} from "@jsonforms/core";
import {
  vanillaRenderers,
  tableArrayControlTester,
} from "@jsonforms/vanilla-renderers";

import { AccordionContainerRenderer } from "./controls/AccordionContainer";
import { ActionListControlRenderer } from "./controls/ActionListControl";
import { ActionListDisplayRenderer } from "./controls/ActionListDisplay";
import { AddressFormRenderer } from "./controls/AddressFormControl";
import { BooleanRadioGroupRenderer } from "./controls/BooleanRadioGroupControl";
import { CheckPurposesRenderer } from "./controls/CheckPurposeControl";
import { CheckboxRenderer } from "./controls/CheckboxControl";
import { CheckboxGroupRenderer } from "./controls/CheckboxGroupControl";
import { ComplianceCheckboxRenderer } from "./controls/ComplianceCheckboxControl";
import { CountrySelectRenderer } from "./controls/CountrySelectControl";
import { NationalitySelectRenderer } from "./controls/NationalitySelectControl";
import { CreditScoreRenderer } from "./controls/CreditScore";
import { CurrencyAndIncomeAmountControlRenderer } from "./controls/CurrencyAndIncomeAmountControl";
import { CurrentDateControlRenderer } from "./controls/CurrentDateControl";
import { DataDisplayListRenderer } from "./controls/DataDisplayList";
import { DataDisplayTableRenderer } from "./controls/DataDisplayTable";
import { DataFieldControlRenderer } from "./controls/DataFieldControl";
import { DateFieldRenderer } from "./controls/DateFieldControl";
import { DateSelectRenderer } from "./controls/DateSelectControl";
import { DividerRenderer } from "./controls/Divider";
import { DriversLicensePreviewRenderer } from "./controls/DriversLicensePreviewControl";
import { EducationRenderer } from "./controls/EducationControl";
import { EducationInstitutionRenderer } from "./controls/EducationInstitutionControl";
import { CompanyNameControlRenderer } from "./controls/CompanyNameControl";
import { EndDateControlRenderer } from "./controls/EndDateControl";
import { EnumCheckboxGroupRenderer } from "./controls/EnumCheckboxGroupControl";
import { LinkButtonControlRenderer } from "./controls/LinkButtonControl";
import { ListStringConcatControlRenderer } from "./controls/ListStringConcatControl";
import { MarkdownControlRenderer } from "./controls/MarkdownControl";
import { ModalLinkControl } from "./controls/ModalLinkControl";
import { NumberFieldRenderer } from "./controls/NumberFieldControl";
import { ObjectRenderer } from "./controls/ObjectControl";
import { PhoneNumberInputControlRenderer } from "./controls/PhoneNumberInputControl";
import { QuestionControlRenderer } from "./controls/QuestionControl";
import { RadioGroupRenderer } from "./controls/RadioGroupControl";
import { RegionSelectRenderer } from "./controls/RegionSelectControl";
import { ReportArrayProcessorControlRenderer } from "./controls/ReportArrayProcessorControl";
import { SearchableSelectControlRenderer } from "./controls/SearchableSelectControl";
import { SectionMessageRenderer } from "./controls/SectionMessage";
import { SectionMessageControlRenderer } from "./controls/SectionMessageControl";
import { SectionSubheadingRenderer } from "./controls/SectionSubheadingControl";
import { SelectControlRenderer } from "./controls/SelectControl";
import { StatsTileRenderer } from "./controls/StatsTile";
import { StatsTilesContainerRenderer } from "./controls/StatsTilesContainer";
import { SuggestionSelectControlRenderer } from "./controls/SuggestionSelectControl";
import { TextAreaControlRenderer } from "./controls/TextAreaControl";
import { TextDisplayRenderer } from "./controls/TextDisplay";
import { TextFieldControlRenderer } from "./controls/TextFieldControl";
import { TextWithLinkModalControlRenderer } from "./controls/TextWithLinkModalControl";
import { ToggleControlRenderer } from "./controls/ToggleControl";
import { UrlControlRenderer } from "./controls/UrlControlRenderer";
import { XYGraphRenderer } from "./controls/XYGraphControl";
import { ArrayLayoutRenderer } from "./layouts/ArrayLayout";
import { CategorizationLayoutRenderer } from "./layouts/CategorizationLayout";
import { CategoryLayoutRenderer } from "./layouts/CategoryLayout";
import { CollapsibleCardLayoutRenderer } from "./layouts/CollapsibleCardLayout";
import { GroupLayoutRenderer } from "./layouts/GroupLayout";
import { HorizontalLayoutRenderer } from "./layouts/HorizontalLayout";
import { ReportLayoutRenderer } from "./layouts/ReportLayout";
import { VerticalLayoutRenderer } from "./layouts/VerticalLayout";

export const renderers: JsonFormsRendererRegistryEntry[] = [
  {
    tester: tableArrayControlTester,
    renderer: ArrayLayoutRenderer,
  },
  ...vanillaRenderers,
  {
    tester: rankWith(3, schemaTypeIs("integer")),
    renderer: NumberFieldRenderer,
  },
  {
    tester: rankWith(3, schemaTypeIs("number")),
    renderer: NumberFieldRenderer,
  },
  {
    tester: rankWith(3, optionIs("multiline", true)),
    renderer: TextAreaControlRenderer,
  },
  {
    tester: rankWith(3, schemaTypeIs("string")),
    renderer: TextFieldControlRenderer,
  },
  {
    tester: rankWith(4, uiTypeIs("DateSelectControl")),
    renderer: DateSelectRenderer,
  },
  {
    tester: rankWith(4, uiTypeIs("PhoneNumberControl")),
    renderer: PhoneNumberInputControlRenderer,
  },
  {
    tester: rankWith(4, formatIs("date")),
    renderer: DateFieldRenderer,
  },
  {
    tester: rankWith(3, isObjectControl),
    renderer: ObjectRenderer,
  },
  {
    tester: rankWith(3, uiTypeIs("Group")),
    renderer: GroupLayoutRenderer,
  },
  {
    tester: rankWith(5, uiTypeIs("Categorization")),
    renderer: CategorizationLayoutRenderer,
  },
  {
    tester: rankWith(3, uiTypeIs("Category")),
    renderer: CategoryLayoutRenderer,
  },
  {
    tester: rankWith(3, uiTypeIs("VerticalLayout")),
    renderer: VerticalLayoutRenderer,
  },
  {
    tester: rankWith(3, uiTypeIs("CollapsibleCardLayout")),
    renderer: CollapsibleCardLayoutRenderer,
  },
  {
    tester: rankWith(3, uiTypeIs("HorizontalLayout")),
    renderer: HorizontalLayoutRenderer,
  },
  {
    tester: rankWith(3, uiTypeIs("ReportLayout")),
    renderer: ReportLayoutRenderer,
  },
  {
    tester: rankWith(4, or(isOneOfEnumControl, isOneOfControl)),
    renderer: SelectControlRenderer,
  },
  {
    tester: rankWith(5, and(isOneOfEnumControl, optionIs("format", "radio"))),
    renderer: RadioGroupRenderer,
  },
  {
    tester: rankWith(
      5,
      and(isOneOfEnumControl, optionIs("format", "checkbox"))
    ),
    renderer: EnumCheckboxGroupRenderer,
  },
  {
    tester: rankWith(5, isBooleanControl),
    renderer: CheckboxRenderer,
  },
  {
    tester: rankWith(5, uiTypeIs("CheckboxGroupControl")),
    renderer: CheckboxGroupRenderer,
  },
  {
    tester: rankWith(5, uiTypeIs("ComplianceCheckboxControl")),
    renderer: ComplianceCheckboxRenderer,
  },
  {
    tester: rankWith(6, and(isBooleanControl, optionIs("type", "toggle"))),
    renderer: ToggleControlRenderer,
  },
  {
    tester: rankWith(6, and(isBooleanControl, optionIs("format", "radio"))),
    renderer: BooleanRadioGroupRenderer,
  },
  {
    tester: rankWith(6, and(isBooleanControl, optionIs("type", "question"))),
    renderer: QuestionControlRenderer,
  },
  {
    tester: rankWith(5, isObjectArrayWithNesting),
    renderer: ArrayLayoutRenderer,
  },
  {
    tester: rankWith(4, isArrayObjectControl),
    renderer: ArrayLayoutRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("AddressControl")),
    renderer: AddressFormRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("ActionListControl")),
    renderer: ActionListControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("DriversLicensePreviewControl")),
    renderer: DriversLicensePreviewRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("RegionSelectControl")),
    renderer: RegionSelectRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("EducationInstitutionControl")),
    renderer: EducationInstitutionRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("CompanyNameControl")),
    renderer: CompanyNameControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("EducationControl")),
    renderer: EducationRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("CountrySelectControl")),
    renderer: CountrySelectRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("NationalitySelectControl")),
    renderer: NationalitySelectRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("SearchableSelectControl")),
    renderer: SearchableSelectControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("EndDateControl")),
    renderer: EndDateControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("URLControl")),
    renderer: UrlControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("CurrentDateControl")),
    renderer: CurrentDateControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("DataDisplayTable")),
    renderer: DataDisplayTableRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("DataDisplayList")),
    renderer: DataDisplayListRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("MarkdownControl")),
    renderer: MarkdownControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("DataFieldControl")),
    renderer: DataFieldControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("ListStringConcatControl")),
    renderer: ListStringConcatControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("StatsTile")),
    renderer: StatsTileRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("StatsTilesContainer")),
    renderer: StatsTilesContainerRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("TextDisplay")),
    renderer: TextDisplayRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("ReportArrayProcessorControl")),
    renderer: ReportArrayProcessorControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("CurrencyAndIncomeAmountControl")),
    renderer: CurrencyAndIncomeAmountControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("AccordionContainer")),
    renderer: AccordionContainerRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("CreditScore")),
    renderer: CreditScoreRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("Divider")),
    renderer: DividerRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("SectionSubheading")),
    renderer: SectionSubheadingRenderer,
  },
  // TODO - this is very similarly named to ActionListControl, but this will be moving to the Report package soon so we're okay with the similarity
  {
    tester: rankWith(10, uiTypeIs("ActionList")),
    renderer: ActionListDisplayRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("SectionMessage")),
    renderer: SectionMessageRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("ModalLinkControl")),
    renderer: ModalLinkControl,
  },
  {
    tester: rankWith(10, uiTypeIs("XYGraph")),
    renderer: XYGraphRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("SectionMessageControl")),
    renderer: SectionMessageControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("TextWithLinkModal")),
    renderer: TextWithLinkModalControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("SuggestionSelectControl")),
    renderer: SuggestionSelectControlRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("CheckPurposesControl")),
    renderer: CheckPurposesRenderer,
  },
  {
    tester: rankWith(10, uiTypeIs("LinkButtonControl")),
    renderer: LinkButtonControlRenderer,
  },
];
